import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Code, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { useImportChart, type ImportAccountRow } from './accountsApi';

interface Props {
  opened: boolean;
  onClose: () => void;
}

// Parses pasted CSV: code,nameAr,nameEn,parentCode,currency (header optional).
function parseCsv(text: string): ImportAccountRow[] {
  const rows: ImportAccountRow[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const cells = line.split(',').map((c) => c.trim());
    if (cells[0]?.toLowerCase() === 'code') continue; // header
    const [code, nameAr, nameEn, parentCode, currencyCode] = cells;
    if (!code || !nameAr || !parentCode) continue;
    rows.push({ code, nameAr, nameEn: nameEn || null, parentCode, currencyCode: currencyCode || null });
  }
  return rows;
}

export function ImportChartModal({ opened, onClose }: Props) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const importChart = useImportChart();
  const rows = useMemo(() => parseCsv(text), [text]);

  const submit = async () => {
    if (rows.length === 0) return;
    try {
      const res = await importChart.mutateAsync(rows);
      notifications.show({ color: 'green', title: t('accounting.import.done'), message: t('accounting.import.createdN', { count: res.created }) });
      setText('');
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red', title: t('common.toasts.errorTitle'),
        message: (isAxiosError(err) && (err.response?.data?.detail as string)) || t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} centered size="lg" title={
      <Text fw={700} c="var(--wheels-ink-strong)">{t('accounting.import.title')}</Text>
    }>
      <Stack gap="md">
        <Alert icon={<IconInfoCircle size={16} stroke={1.6} />} color="gray" variant="light" radius="sm" py="xs">
          <Text size="xs" style={{ whiteSpace: 'pre-line' }}>{t('accounting.import.help')}</Text>
          <Code block mt={6} dir="ltr">code,nameAr,nameEn,parentCode,currency{'\n'}1105,صندوق فرعي,Petty Cash,1100,ILS</Code>
        </Alert>
        <Textarea
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
          placeholder="1105,صندوق فرعي,Petty Cash,1100"
          autosize
          minRows={6}
          maxRows={16}
          dir="ltr"
          styles={{ input: { fontFamily: 'monospace', fontSize: 13 } }}
        />
        <Group justify="space-between">
          <Text size="sm" c="dimmed" className="font-numeric">{t('accounting.import.rowsParsed', { count: rows.length })}</Text>
          <Group gap="sm">
            <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
            <Button onClick={submit} loading={importChart.isPending} disabled={rows.length === 0}>
              {t('accounting.import.submit')}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
